/**
 * ACHAIGenerator
 * Iframe wrapper for the AI content generation page (/embed/ai).
 * Follows the same CSS isolation pattern as ACHEditor:
 *
 *   ContentDetailsEditor (web-content, Ant Design)
 *     -> ACHAIGenerator (shared-components)
 *       -> iframe (src = {VITE_ACH_EMBED_URL}/embed/ai?...)
 *         -> EmbedAIPage (editor app, Tailwind CSS)
 *           -> AIContentGenerator + PromptFormRenderer
 *
 * PostMessage protocol uses AI_* prefix.
 */

import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { ACH_EMBED_IFRAME_ALLOW } from '@ai-content-hub/content-core';
import { getCachedCustomTheme } from '@ai-content-hub/shared-styles';

export type AIIframeHeightMode = 'content' | 'container';

export interface ACHAIGeneratorProps {
    contentId?: string;
    questionType: string;
    tenantId?: string;
    apiConfig?: { baseUrl?: string; getAuthToken?: () => string | null };
    onJobCreated?: (jobId: string) => void;
    onJobCompleted?: (result: { contentId?: string; metaId?: string }) => void;
    onError?: (error: string) => void;
    minHeight?: number;
    /** `container` fills a fixed parent and ignores embed height pings (prevents resize loops). */
    iframeHeightMode?: AIIframeHeightMode;
    className?: string;
    /** Translated iframe title attribute. Defaults to 'AI Content Generator'. */
    iframeTitle?: string;
    /** BCP-47 or short locale (e.g. vi, en) — syncs prompt-form i18n inside embed. */
    language?: string;
}

type AIMessageType =
    | 'AI_READY'
    | 'AI_HEIGHT_CHANGE'
    | 'AI_JOB_CREATED'
    | 'AI_JOB_COMPLETED'
    | 'AI_ERROR';

interface AIMessage {
    source?: string;
    type: AIMessageType;
    payload?: Record<string, unknown>;
}

export const ACHAIGenerator: React.FC<ACHAIGeneratorProps> = ({
    contentId,
    questionType,
    tenantId,
    apiConfig,
    onJobCreated,
    onJobCompleted,
    onError,
    minHeight = 400,
    iframeHeightMode = 'content',
    className,
    iframeTitle = 'AI Content Generator',
    language,
}) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const lastHeightRef = useRef(minHeight);
    const heightCooldownUntilRef = useRef(0);
    const [iframeHeight, setIframeHeight] = useState(minHeight);
    const [isReady, setIsReady] = useState(false);
    const useContentHeight = iframeHeightMode === 'content';

    const HEIGHT_CHANGE_THRESHOLD = 20;
    const HEIGHT_COOLDOWN_MS = 600;

    const embedUrl = useMemo(
        () =>
            typeof import.meta !== 'undefined'
                ? (import.meta.env?.VITE_ACH_EMBED_URL as string | undefined)
                : undefined,
        []
    );

    const iframeSrc = useMemo(() => {
        if (!embedUrl) return null;

        const params = new URLSearchParams();
        if (contentId) params.set('contentId', contentId);
        if (tenantId) params.set('tenantId', tenantId);
        params.set('hideQuestionTypeSelector', '1');
        // WHY: questionType and language are sent via AI_INIT / AI_CONFIG_UPDATE so
        // switching the header dropdown does not reload the iframe (full reload +
        // h-screen scrollHeight feedback used to freeze the page).

        const token = apiConfig?.getAuthToken?.();
        if (token) params.set('apiToken', token);
        if (apiConfig?.baseUrl) params.set('baseUrl', apiConfig.baseUrl);

        // Pass custom theme config so the editor iframe can apply it on load
        // EAA-1664: tenant-scoped cache — without tenantId this read the
        // empty legacy slot and the iframe never got the tenant's override.
        const themeConfig = getCachedCustomTheme(tenantId);
        if (themeConfig) {
            try {
                params.set('theme', btoa(JSON.stringify(themeConfig)));
            } catch {
                /* ignore encoding errors */
            }
        }

        const query = params.toString();
        return `${embedUrl}/embed/ai${query ? `?${query}` : ''}`;
    }, [embedUrl, contentId, tenantId, apiConfig]);

    // WHY: iframe `src` identity changed — old embed is gone; wait for AI_READY again.
    useEffect(() => {
        setIsReady(false);
    }, [iframeSrc]);

    const sendToIframe = useCallback((type: string, payload?: unknown) => {
        iframeRef.current?.contentWindow?.postMessage(
            { source: 'ach-ai-generator', type, payload },
            '*'
        );
    }, []);

    // Send AI_INIT when iframe is ready
    useEffect(() => {
        if (!isReady) return;

        const token = apiConfig?.getAuthToken?.();
        sendToIframe('AI_INIT', {
            baseUrl: apiConfig?.baseUrl,
            apiToken: token,
            tenantId,
            contentType: questionType,
            contentId,
            language,
        });

        // Also send custom theme via postMessage so the editor iframe applies it
        // EAA-1664: tenant-scoped cache — see comment above.
        const themeConfig = getCachedCustomTheme(tenantId);
        iframeRef.current?.contentWindow?.postMessage(
            { type: 'custom-theme', config: themeConfig },
            '*'
        );
    }, [isReady, apiConfig, tenantId, questionType, contentId, language, sendToIframe]);

    // Send config update when questionType changes after initial ready
    useEffect(() => {
        if (!isReady) return;
        sendToIframe('AI_CONFIG_UPDATE', { questionType, contentId });
    }, [isReady, questionType, contentId, sendToIframe]);

    // Listen for messages from iframe
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const msg = event.data as AIMessage;
            if (!msg || msg.source !== 'embed-ai') return;

            switch (msg.type) {
                case 'AI_READY':
                    setIsReady(true);
                    break;

                case 'AI_HEIGHT_CHANGE': {
                    if (!useContentHeight) break;
                    const height = msg.payload?.height as number | undefined;
                    if (!height || height <= 0) break;
                    const newHeight = Math.max(height, minHeight);
                    const isSignificantGrowth =
                        newHeight - lastHeightRef.current > HEIGHT_CHANGE_THRESHOLD;
                    const inCooldown = Date.now() < heightCooldownUntilRef.current;
                    if (inCooldown && !isSignificantGrowth) break;
                    if (
                        Math.abs(newHeight - lastHeightRef.current) > HEIGHT_CHANGE_THRESHOLD ||
                        isSignificantGrowth
                    ) {
                        lastHeightRef.current = newHeight;
                        heightCooldownUntilRef.current = Date.now() + HEIGHT_COOLDOWN_MS;
                        setIframeHeight(newHeight);
                    }
                    break;
                }

                case 'AI_JOB_CREATED': {
                    const jobId = msg.payload?.jobId as string | undefined;
                    if (jobId) onJobCreated?.(jobId);
                    break;
                }

                case 'AI_JOB_COMPLETED': {
                    onJobCompleted?.({
                        contentId: msg.payload?.contentId as string | undefined,
                        metaId: msg.payload?.metaId as string | undefined,
                    });
                    break;
                }

                case 'AI_ERROR': {
                    const message = msg.payload?.message as string | undefined;
                    if (message) onError?.(message);
                    break;
                }
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [minHeight, onJobCreated, onJobCompleted, onError, useContentHeight]);

    if (!embedUrl || !iframeSrc) {
        return (
            <div
                className={`ach-ai-generator-fallback ${className || ''}`}
                style={{
                    padding: 24,
                    textAlign: 'center',
                    minHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6b7280',
                    background: '#f9fafb',
                    borderRadius: 8,
                    border: '1px dashed #d1d5db',
                }}
            >
                AI Generator embed URL not configured. Set VITE_ACH_EMBED_URL environment variable.
            </div>
        );
    }

    return (
        <div
            className={`ach-ai-generator-container ${className || ''}`}
            style={{ position: 'relative', width: '100%', height: '100%' }}
        >
            <iframe
                ref={iframeRef}
                src={iframeSrc}
                title={iframeTitle}
                allow={ACH_EMBED_IFRAME_ALLOW}
                style={{
                    width: '100%',
                    ...(useContentHeight
                        ? { height: iframeHeight, minHeight: iframeHeight }
                        : { height: '100%', minHeight: 0 }),
                    border: 'none',
                    display: 'block',
                }}
            />
        </div>
    );
};

ACHAIGenerator.displayName = 'ACHAIGenerator';

export default ACHAIGenerator;
